/**
 * This module extends the Leaflet library in order to display a TomTom map. For full reference of the Leaflet map API check <a
 * href="http://leafletjs.com/reference.html" target="_blank">here</a>.
 *
 * @example
 *  var map = L.map("map", {key: "<Your API key>"});
 *
 * @class L
 */
/**
 * Initializes the map. - .map()
 *
 *
 * ```html
 *
 * <style type="text/css">
 * html, body {
 *   width: 100%;
 *   height: 100%;
 *   margin: 0;
 *   padding: 0;
 * }
 * #map {
 *   width: 100%;
 *   height: 100%;
 * }
 * </style>
 * <div id="map"></div>
 * <script>
 *     var map = L.map("map", {key: "<Your API key>"});
 * </script>
 * ```
 * @method map
 * @param {HTMLElement|String} id The div element or its id in which the map will be initialized.
 * @param {Object} [options] All the options that leaflet allows, as described here:
 *     http://leafletjs.com/reference.html#map-options Additional options, which are TomTom specific are described
 *     below.
 * @param {String} [options.key] This is an API key that can be obtained form http://developer.tomtom.com When the key
 *     is passed on the map's initialization, it is set globally for all the services. It can be overwritten with the
 *     tomtom.key method globally or per service  with tomtom.&lt;service_name&gt;.key functions. It is also possible
 *     to pass a specific key while working with instances of the services.
 * @param {String} [options.style] The base map style to be used. Should be set to one of the following values:
 *     &lt;'main', 'night'&gt;. By default, the _main_ style is used.
 * @param {String} [options.layer] The base map's layer to be used. Should be set to one of the following values:
 *     &lt;'basic', 'hybrid', 'labels'&gt;. By default, the _basic_ layer is used.
 * @param {Boolean|Object} [options.traffic] Allows to add L.TomTomTrafficLayer and L.TomTomTrafficIncidentsLayer to
 *     the map. A value of this parameter can be set to an options object as described in the traffic layer
 *     documentation. If traffic layers are added with this option, a mechanism for periodic refresh of the traffic
 *     data is provided (using a proper traffic model id handling).
 * @param {Boolean} [options.traffic.noRefresh] Disables periodic updates of traffic layers.
 * @param {Boolean|Object} [options.trafficFlow] Allows to add L.TomTomTrafficFlowLayer to the map. A value of this
 *     parameter can be set to the options object as described in the traffic flow layer documentation.
 * @param {Boolean} [options.noLabelsOnTop=false] If a traffic or traffic flow layer is overlayed on the map, then by
 *     default, the labels layer is added on top of these layers. It prevents labels from being covered by the traffic
 *     tubes or traffic flow. With this option, it is possible to disable this behaviour.
 * @return {map} Returns the Leaflet map object.
 */
define('display/map',['require','leaflet','./svgIcon','./imagePath','./layers/tileLayer','./layers/trafficLayer','./layers/trafficFlowLayer','./layers/labelsLayer','security','services/copyrightCaption','./copyright'],function (require) {
    "use strict";

    var L = require("leaflet"),
        svgIcon = require("./svgIcon"),
        imagePath = require("./imagePath");

    L.Map.mergeOptions({
        worldCopyJump: true
    });

    L.Control.Attribution.prototype.options = {
        position: "bottomleft",
        prefix: ""
    };

    L.Icon.Default = svgIcon;
    L.Icon.Default.imagePath = imagePath.get();
    L.Marker.mergeOptions({icon: new L.Icon.Default()});

    function isOlderThanIE11() {
        return window.navigator.userAgent.indexOf('MSIE ') > -1;
    }

    function initBaseLayer(map) {
        var baseMap = require("./layers/tileLayer");

        if (!map.options.layers) {
            map.options.layers = [];
            map.options.layers.push(baseMap(map.options));
        }
    }

    function initTrafficLayer(map) {
        var traffic = require("./layers/trafficLayer");


        if (map.options.traffic) {
            map.options.layers.push(traffic(map.options.traffic));
        }
    }

    function initTrafficFlowLayer(map) {
        var trafficFlow = require("./layers/trafficFlowLayer");

        if (map.options.trafficFlow) {
            map.options.layers.push(trafficFlow(map.options.trafficFlow));
        }
    }

    function initLabelsLayers(map) {
        var labelsLayer = require("./layers/labelsLayer");

        if (!map.options.noLabelsOnTop && !isOlderThanIE11()) {
            //This will not work on IE < 11 due to not supported CSS: pointer-events: none;
            map.options.layers.push(labelsLayer(map.options));
        }
    }

    function initHook() {
        var map = this,
            security = require("security"),
            copyrightCaption = require("services/copyrightCaption"),
            copyright = require("./copyright");

        security.setKey(map.options.key);
        initBaseLayer(map);
        initTrafficLayer(map);
        initTrafficFlowLayer(map);
        initLabelsLayers(map);

        if (map.getZoom() === undefined) {
            map.setView([0, 0], 2);
        }
        if (!map.options.maxBounds) {
            map.setMaxBounds(L.latLngBounds([[-90, -18000], [90, 18000]]));
        }
        copyrightCaption().go(function (attribution) {
            map.attributionControl.addAttribution("<a href='#' class='tomtomAttribution'>" + attribution + "</a>");
            copyright.addTo(map);
        });
    }

    L.Map.addInitHook(initHook);

    return L;
});
